/* eslint-disable no-param-reassign */
import { createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit'

import {
  BUILTIN_PRESETS,
  DEFAULT_COLUMN_ARRANGEMENT,
  DEFAULT_ORDER_VERSION,
  MIN_COLUMN_WIDTH,
  normalizeArrangement,
  type ColumnArrangement,
  type PersistedArrangement,
  type ProductivityColumnId,
  type ProductivityColumnPreset,
  type SheetMode,
} from 'containers/new-route-manager/route-productivity/productivityColumns'
import { DEFAULT_PRODUCTIVITY_DENSITY, type ProductivityDensity } from 'containers/new-route-manager/route-productivity/productivityDensity'

import type { ProductivityViewMode } from 'containers/new-route-manager/route-productivity/productivitySheetTypes'

// Persisted to localStorage via redux-persist (whitelisted in providers/store),
// mirroring persistentListViewSlice.
export const PERSISTENT_ROUTE_PRODUCTIVITY_COLUMNS_KEY = 'persistentRouteProductivityColumns' as const

interface PersistentColumnsState {
  viewMode: ProductivityViewMode
  compareViewMode: ProductivityViewMode
  density: ProductivityDensity
  layoutVersion: number
  // Read back through `normalizeArrangement`, so a layout persisted by an older
  // release (a single-mode `{order,pinned,hidden}`) still resolves.
  arrangement: PersistedArrangement
  presets: ProductivityColumnPreset[]
  activePresetId: string | null
}

const initialState: PersistentColumnsState = {
  viewMode: 'table',
  compareViewMode: 'cards',
  density: DEFAULT_PRODUCTIVITY_DENSITY,
  layoutVersion: DEFAULT_ORDER_VERSION,
  arrangement: DEFAULT_COLUMN_ARRANGEMENT,
  presets: [],
  activePresetId: null,
}

/**
 * The layout to render. One saved against an older default ORDER is dropped rather
 * than normalized: `normalizeArrangement` preserves a saved order and only slots new
 * columns in beside a neighbour, so a regrouped default would otherwise never reach
 * anyone who had already opened the sheet. Every reducer that writes an arrangement
 * re-stamps the version, so a deliberate layout survives the next regroup.
 */
const effectiveArrangement = (state: PersistentColumnsState): ColumnArrangement =>
  normalizeArrangement(state.layoutVersion === DEFAULT_ORDER_VERSION ? state.arrangement : DEFAULT_COLUMN_ARRANGEMENT)

const persistentRouteProductivityColumnsSlice = createSlice({
  name: PERSISTENT_ROUTE_PRODUCTIVITY_COLUMNS_KEY,
  initialState,
  reducers: {
    setProductivityViewMode: (state, action: PayloadAction<{ viewMode: ProductivityViewMode; isComparing: boolean }>) => {
      const { viewMode, isComparing } = action.payload
      if (isComparing) state.compareViewMode = viewMode
      else state.viewMode = viewMode
    },
    setProductivityDensity: (state, action: PayloadAction<ProductivityDensity>) => {
      state.density = action.payload
    },
    setColumnArrangement: (state, action: PayloadAction<ColumnArrangement>) => {
      state.arrangement = normalizeArrangement(action.payload)
      state.layoutVersion = DEFAULT_ORDER_VERSION
      // A manual change detaches from whatever preset was active.
      state.activePresetId = null
    },
    // Dragging a column header writes the WHOLE resolved order back rather than a
    // from/to index pair: the grid's order is pinned-first and carries the
    // identity and cluster tracks, so an index into it doesn't line up with the
    // stored order.
    setColumnOrder: (state, action: PayloadAction<{ mode: SheetMode; orderedIds: ProductivityColumnId[] }>) => {
      const { mode, orderedIds } = action.payload
      const next = effectiveArrangement(state)
      next[mode] = { ...next[mode], order: orderedIds }
      state.arrangement = normalizeArrangement(next)
      state.layoutVersion = DEFAULT_ORDER_VERSION
      state.activePresetId = null
    },
    setColumnWidth: (state, action: PayloadAction<{ mode: SheetMode; columnId: ProductivityColumnId; width: number }>) => {
      const { mode, columnId, width } = action.payload
      const next = effectiveArrangement(state)
      next[mode] = { ...next[mode], widths: { ...next[mode].widths, [columnId]: Math.max(MIN_COLUMN_WIDTH, Math.round(width)) } }
      state.arrangement = next
      state.layoutVersion = DEFAULT_ORDER_VERSION
      state.activePresetId = null
    },
    applyColumnPreset: (state, action: PayloadAction<string>) => {
      const preset = [...BUILTIN_PRESETS, ...state.presets].find(entry => entry.id === action.payload)
      if (!preset) return
      state.arrangement = normalizeArrangement(preset.arrangement)
      state.layoutVersion = DEFAULT_ORDER_VERSION
      state.activePresetId = preset.id
    },
    saveColumnPreset: (state, action: PayloadAction<{ id: string; name: string }>) => {
      const { id, name } = action.payload
      // Captures the layout the office is LOOKING at, which is not the stored one
      // when that was stamped against an older default order.
      state.presets.push({ id, name, arrangement: effectiveArrangement(state) })
      state.activePresetId = id
    },
    deleteColumnPreset: (state, action: PayloadAction<string>) => {
      state.presets = state.presets.filter(entry => entry.id !== action.payload)
      if (state.activePresetId === action.payload) state.activePresetId = null
    },
    resetColumnArrangement: state => {
      state.arrangement = DEFAULT_COLUMN_ARRANGEMENT
      state.layoutVersion = DEFAULT_ORDER_VERSION
      state.activePresetId = null
    },
  },
})

export const {
  setProductivityViewMode,
  setProductivityDensity,
  setColumnArrangement,
  setColumnOrder,
  setColumnWidth,
  applyColumnPreset,
  saveColumnPreset,
  deleteColumnPreset,
  resetColumnArrangement,
} = persistentRouteProductivityColumnsSlice.actions

interface RootStateSlice {
  [PERSISTENT_ROUTE_PRODUCTIVITY_COLUMNS_KEY]?: PersistentColumnsState
}

const selectState = (state: RootStateSlice): PersistentColumnsState => state[PERSISTENT_ROUTE_PRODUCTIVITY_COLUMNS_KEY] ?? initialState

// The persist merge replaces the whole slice, so a field added after a user's last save reads undefined.
export const selectProductivityViewMode = (state: RootStateSlice, isComparing: boolean): ProductivityViewMode =>
  isComparing ? selectState(state).compareViewMode ?? initialState.compareViewMode : selectState(state).viewMode ?? initialState.viewMode

export const selectProductivityDensity = (state: RootStateSlice): ProductivityDensity => selectState(state).density ?? initialState.density

// Memoized: normalizing allocates a fresh arrangement, and an unstable identity
// here re-renders every row of the sheet on any unrelated store change.
export const selectColumnArrangement = createSelector((state: RootStateSlice) => selectState(state), effectiveArrangement)
export const selectColumnPresets = (state: RootStateSlice): ProductivityColumnPreset[] => selectState(state).presets
export const selectActiveColumnPresetId = (state: RootStateSlice): string | null => selectState(state).activePresetId

export default persistentRouteProductivityColumnsSlice.reducer
